import AsyncSelect from "react-select/async";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormControl, FormHelperText } from "@mui/material";

import { fetchVehicleModels } from "../../service";
import InputRequired from "../../../common/InputRequired";

const VehicleModels = ({ brand, setValue, errors = {} }) => {
    const [models, setModels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);

    /* ------------------ Reset model when brand changes ------------------ */
    useEffect(() => {
        if (!brand) return;

        setSelectedOption(null);
        setModels([]);
        setValue("ftm_vehicle_model", null, { shouldValidate: true });
    }, [brand, setValue]);

    /* ------------------ Fetch models ------------------ */
    useEffect(() => {
        if (!brand) return;

        let isMounted = true;

        const fetchModels = async () => {
            try {
                setLoading(true);
                const res = await fetchVehicleModels(brand);

                if (isMounted && res?.Results?.length) {
                    setModels(res.Results);
                }
            } catch (err) {
                console.error("Fetch vehicle models error:", err);
            } finally {
                isMounted && setLoading(false);
            }
        };

        fetchModels();

        return () => {
            isMounted = false;
        };
    }, [brand]);

    /* ------------------ Options ------------------ */
    const options = useMemo(
        () =>
            models.map(({ Model_Name }) => ({
                value: Model_Name,
                label: Model_Name,
            })),
        [models]
    );

    /* ------------------ Async loader (FIXED) ------------------ */
    const loadOptions = useCallback(
        (inputValue) => {
            if (!inputValue) {
                return Promise.resolve(options);
            }

            const search = inputValue.toLowerCase();
            const filtered = options.filter(opt =>
                opt.label.toLowerCase().includes(search)
            );

            return Promise.resolve(filtered);
        },
        [options]
    );

    /* ------------------ Change handler ------------------ */
    const handleChange = useCallback(
        (option) => {
            setSelectedOption(option);

            const value = option?.value || null;
            setValue("ftm_vehicle_model", value, { shouldValidate: true });
        },
        [setValue]
    );

    /* ------------------ Styles ------------------ */
    const selectStyles = useMemo(
        () => ({
            control: (base) => ({
                ...base,
                width: "100%",
            }),
            option: (base, state) => ({
                ...base,
                backgroundColor:
                    state.isSelected || state.isFocused ? "#8bd68fff" : "#fff",
                color:
                    state.isSelected || state.isFocused ? "#fff" : "#333",
            }),
        }),
        []
    );

    return (
        <FormControl error={!!errors?.ftm_vehicle_model} fullWidth>
            <div className="mt-1 mb-3">
                <strong>
                    Vehicle models <InputRequired />
                </strong>

                <AsyncSelect
                    cacheOptions
                    defaultOptions={options}
                    loadOptions={loadOptions}
                    value={selectedOption}
                    placeholder="Select vehicle model"
                    isClearable
                    isDisabled={!brand}
                    isLoading={loading}
                    onChange={handleChange}
                    styles={selectStyles}
                />

                {errors?.ftm_vehicle_model && (
                    <FormHelperText>
                        {errors.ftm_vehicle_model.message}
                    </FormHelperText>
                )}
            </div>
        </FormControl>
    );
};

export default VehicleModels;